import { Dialog, DialogContent, DialogActions, TextField, Alert, IconButton, InputAdornment } from "@mui/material";
import CustomPrimaryButton from "@fuse/core/CustomPrimaryButton/CustomPrimaryButton";
import { useNavigate } from "react-router";
import { useLayoutEffect, useState } from "react";
import petService from "@fuse/services/petService";
import paymentService from "@fuse/services/paymentService";
import { IPetModel } from "@fuse/models";
import { IApiResponse } from "@fuse/models/user.models";
import { Typography } from "@mui/material";
import { useAppSelector } from "app/store";
import { selectPeevaSettings } from "app/store/fuse/peevaSettings";
import PlanType from "src/app/main/auth/types/plan.type";
import FuseLoading from "@fuse/core/FuseLoading";
import jwtService from "src/app/main/auth/services/jwtService";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import FieldLabel from "@fuse/core/FieldLabel/FieldLabel";

const MIN_PASSWORD_LENGTH = 6;

function ActivatePetModal({ petId, isRecoverMode = false }) {
	const navigate = useNavigate();
	const appSettings = useAppSelector(selectPeevaSettings);
	const [pet, setPet] = useState<IPetModel>({} as IPetModel);
	const [customError, setCustomError] = useState("");
	const [loading, setLoading] = useState(false);
	const [plans, setPlans] = useState<PlanType[]>([]);

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [passwordError, setPasswordError] = useState("");

	function loadPlans() {
		return petService
			.getPetPlans()
			.then((res: any) => {
				if (res.result) {
					setPlans(res.result.plans);

					const fetchedPlans = res.result.plans as Array<PlanType>;
					fetchedPlans.forEach((p) => {
						selectedPlansPush(p);
					});

					onPlansView(fetchedPlans);
					return fetchedPlans;
				}
				return [];
			});
	}

	useLayoutEffect(() => {
		const fetchPet = isRecoverMode
			? petService.getPetByIdAsGuest(petId)
			: petService.getPetById(petId);

		fetchPet
			.then((res: any) => {
				if (res.result) {
					setPet(res.result);
				}
			})
			.catch((error) => {
				console.log(error);
			});

		if (!isRecoverMode) {
			loadPlans().catch((error) => console.log(error));
		}
	}, []);

	function validatePassword(): boolean {
		if (!password || password.length < MIN_PASSWORD_LENGTH) {
			setPasswordError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
			return false;
		}
		if (password !== confirmPassword) {
			setPasswordError("Passwords do not match.");
			return false;
		}
		setPasswordError("");
		return true;
	}

	async function onCardSelect() {
		if (isRecoverMode) {
			if (!validatePassword()) return;

			setLoading(true);
			try {
				const setPwdResult = await jwtService.setPasswordByPetId(petId, password) as any;
				const email = setPwdResult.email;

				await jwtService.signInWithEmailAndPassword(email, password);

				const fetchedPlans = await loadPlans() as PlanType[];
				await proceedToStripe(fetchedPlans);
			} catch (err: any) {
				const msg = typeof err === "string" ? err : err?.message || "An error occurred.";
				setCustomError(msg);
				setLoading(false);
				setTimeout(() => setCustomError(""), 5000);
			}
		} else {
			await proceedToStripe(plans);
		}
	}

	async function proceedToStripe(activePlans: PlanType[]) {
		setLoading(true);
		const lifetimePlan = activePlans.find((p) => p.oneTime === true);
		const selectedPlanId = lifetimePlan.id;
		const resp = (await petService.updatePetPlan(petId, {
			id: petId,
			planId: selectedPlanId,
		})) as IPetModel;

		if (resp.id) {
			const tagSelected = false;

			const checkout = (await paymentService.getCheckoutUrl(
				resp.id,
				true, tagSelected
			)) as IApiResponse;

			if (checkout.succeeded) {
				setLoading(false);
				location.href = checkout.result.location;
			} else {
				setCustomError(checkout.errors[0]);

				setTimeout(() => {
					setCustomError("");
					setLoading(false);
				}, 5000);
			}
		} else {
			setLoading(false);
		}
	}

	function selectedPlansPush(plan: PlanType) {
		const experimentName = plan.oneTime ? "lifetime" : "yearly";
		//@ts-ignore
		window.dataLayer = window.dataLayer || [];

		//@ts-ignore
		window.dataLayer.push({
			event: "experiment_play",
			experiment_name: experimentName,
			experiment_variant: plan.planPrice,
		});
	}

	function onPlansView(plans: PlanType[]) {
		const plan = plans[0];
		const sum = plans.reduce((t, el) => t + el.planPrice, 0);
		const items = [];
		plans.forEach((p) => {
			items.push({
				item_id: p.planId,
				item_name: p.name,
				item_variant: p.priceId,
				price: p.planPrice,
			});
		});

		//@ts-ignore
		window.dataLayer = window.dataLayer || [];

		//@ts-ignore
		window.dataLayer.push({
			event: "view_item",
			currency: plan.currency,
			value: sum,
			items: [...items],
		});
	}

	return (
		<>
			<Dialog
				open
				maxWidth="sm"
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: 3,
						textAlign: "center",
						p: 2,
						position: "relative",
					},
				}}
			>
				{loading && (
					<div
						style={{
							position: "absolute",
							inset: 0,
							zIndex: 10,
							backgroundColor: "rgba(255,255,255,0.75)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<FuseLoading />
					</div>
				)}
				<DialogContent>
					<img
						src="assets/img/warning.png"
						alt="warning"
						style={{ width: 48, margin: "0 auto 16px" }}
					/>

					<Typography variant="h6" fontWeight={700} gutterBottom>
						Your pet's microchip is NOT linked to you
					</Typography>

					<Typography color="text.secondary">
						If your pet is found today, shelters and veterinarians won't see a verified owner record for this microchip.
						Activate now to link your contact information and add emergency contacts, medical notes, and photos.
					</Typography>

					{isRecoverMode && (
						<div className="flex flex-col gap-[8px] w-full mt-24">
							<FieldLabel>Password</FieldLabel>
							<TextField
								className="mb-20 bg-white rounded-24 h-[44px] md:h-46px text-14px md:text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
								type={showPassword ? "text" : "password"}
								placeholder="Enter your password"
								fullWidth
								variant="outlined"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								sx={{ border: "2px solid black" }}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
							<FieldLabel>Password (Confirm)</FieldLabel>
							<TextField
								className="mb-20 bg-white rounded-24 h-[44px] md:h-46px text-14px md:text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
								type={showConfirmPassword ? "text" : "password"}
								placeholder="Enter your password (confirm)"
								fullWidth
								variant="outlined"
								autoComplete="off"
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								sx={{ border: "2px solid black" }}
								InputProps={{
									endAdornment: (
										<InputAdornment position="end">
											<IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
												{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</div>
					)}

					{(passwordError || customError) && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{passwordError || customError}
						</Alert>
					)}
				</DialogContent>

				<DialogActions sx={{ justifyContent: "center", pb: 2 }}>
					<CustomPrimaryButton
						onClick={() => onCardSelect()}
					>
						Activate and Protect my pet
					</CustomPrimaryButton>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default ActivatePetModal;
